import React, { type FC, useEffect, useMemo, useState } from 'react'
import { LoadingButton } from '@mui/lab'
import { IconButton, InputAdornment, TextField } from '@mui/material'
import { useFormik } from 'formik'
import { type InferType, object, string } from 'yup'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { StoreStatus } from 'common/utils/types'
import { ReactComponent as Eye } from 'common/assets/eye.svg'
import { ReactComponent as EyeCrossed } from 'common/assets/eyeCrossed.svg'
import styles from 'common/navigation/auth-layout/AuthLayout.module.scss'

import { confirmSignUp } from '../auth.service'
import Message from '../Message'

const confirmSignUpSchema = object({
  email: string().email('Invalidan email').required('Obavezno polje').default(''),
  newPassword: string()
    .required('Obavezno polje')
    .default('')
    .min(
      8,
      'Lozinka mora sadržati minimum 8 karaktera, jedno veliko slovo, jedno malo slovo, broj i specijalni karakter',
    )
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[~!@#$%^&*_\-+=`|\\(){}[\]:;"'<>,.?/]).*$/,
      'Lozinka mora sadržati minimum 8 karaktera, jedno veliko slovo, jedno malo slovo, broj i specijalni karakter',
    ),
})

export type ConfirmSignUpForm = InferType<typeof confirmSignUpSchema>

const ConfirmSignUp: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { status, user } = useAppSelector((state) => state.auth)
  const [showPassword, setShowPassword] = useState(false)

  const email: string = useMemo(() => (location.state as Record<string, string>)?.email, [])

  const defaultValues: ConfirmSignUpForm = {
    email: email || '',
    newPassword: '',
  }

  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema: confirmSignUpSchema,
    onSubmit: async (formValues: ConfirmSignUpForm) => await dispatch(confirmSignUp({ user, formValues })),
  })

  useEffect(() => {
    if (!email) {
      navigate('/auth', { replace: true })
    }
  }, [])

  return (
    <div className={styles.container}>
      <h2 className="text-center">Registracija naloga</h2>

      <Message />

      <form onSubmit={formik.handleSubmit} className={styles.formContainer}>
        <TextField
          id="email"
          name="email"
          label="Email"
          placeholder="name@example.com"
          type="email"
          InputProps={{
            readOnly: true,
            classes: {
              root: styles.input,
            },
          }}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.email}
          error={formik.touched.email && !!formik.errors.email}
          helperText={formik.touched.email && formik.errors.email}
          sx={{
            width: '100%',
          }}
        />
        <TextField
          id="newPassword"
          name="newPassword"
          label="Lozinka"
          type={showPassword ? 'text' : 'password'}
          autoFocus={true}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.newPassword}
          error={formik.touched.newPassword && !!formik.errors.newPassword}
          helperText={formik.touched.newPassword && formik.errors.newPassword}
          sx={{
            width: '100%',
          }}
          InputProps={{
            classes: {
              root: styles.input,
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={(e) => {
                    e.preventDefault()
                    setShowPassword((show) => !show)
                    document.getElementById('newPassword')?.focus()
                  }}
                  edge="end"
                >
                  {showPassword ? <Eye /> : <EyeCrossed />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <LoadingButton
          type="submit"
          loading={status === StoreStatus.Loading}
          variant="outlined"
          sx={{
            width: 200,
          }}
        >
          Potvrdi
        </LoadingButton>
      </form>
    </div>
  )
}

export default ConfirmSignUp
